import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import SectionTitle from "@/components/shared/SectionTitle";
import { CalendarCheck2, BookOpen, MessageSquare, ChevronRight,
         Sunset, Sun, Music, BookMarked, Star, FileText } from "lucide-react";
import { getUnreadMemberMessageCount } from "@/lib/api";

import {
  DashboardHeader,
  DashboardHero,
  DashboardSearch,
  SpiritualGrowthCard,
  TodayPriority,
  UpcomingAgenda,
  ImportantDates,
  PastoralPlanToday,
} from "@/features/dashboard/components";

import WelcomeBanner from "@/components/shared/WelcomeBanner";

import { useDistrictGuard } from "@/hooks/useDistrictGuard";
import { useNotificationsCtx } from "../contexts/NotificationsContext";
import { useRegisterKeviaContext } from "../contexts/KeviaContext";
import { useSabbathSchool } from "@/hooks/useSabbathSchool";

import {
  useDashboardData,
  useOfflineStatus,
  useSpiritualProgress,
  useTodayPreaching,
} from "@/features/dashboard/hooks";

/* ── SabbathCountdown — solo visible viernes y sábado ────────────────────── */
function SabbathCountdown() {
  const dayOfWeek = new Date().getDay();
  const [sunsetTime,    setSunsetTime]    = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    if (dayOfWeek !== 5 && dayOfWeek !== 6) return;
    const today   = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;

    const load = async (lat, lng) => {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8000);
        const res  = await fetch(
          `https://api.sunrise-sunset.org/json?lat=${lat}&lng=${lng}&date=${dateStr}&formatted=0`,
          { signal: controller.signal }
        );
        clearTimeout(timer);
        const json = await res.json();
        if (json.status !== "OK") throw new Error("no data");
        setSunsetTime(json.results.sunset);
      } catch { /* silencioso — no mostrar error */ }
    };

    if (!navigator.geolocation) {
      setUsingFallback(true);
      load(28.6353, -106.0889);
    } else {
      navigator.geolocation.getCurrentPosition(
        (pos) => load(pos.coords.latitude, pos.coords.longitude),
        ()    => { setUsingFallback(true); load(28.6353, -106.0889); },
        { timeout: 7000, maximumAge: 300_000 }
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (dayOfWeek !== 5 && dayOfWeek !== 6) return null;

  let timeLabel = null;
  if (sunsetTime) {
    const d   = new Date(sunsetTime);
    let h     = d.getHours();
    const min = String(d.getMinutes()).padStart(2, "0");
    const sfx = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    timeLabel = `${h}:${min} ${sfx}`;
  }

  const label    = dayOfWeek === 5 ? "Inicio del sábado hoy" : "Fin del sábado hoy";
  const timeText = timeLabel ? ` a las ${usingFallback ? "aprox. " : ""}${timeLabel}` : "";

  return (
    <div className="mx-4 mb-3 flex items-center gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-700/40 rounded-[16px] px-4 py-3">
      <Sunset size={15} className="text-amber-600 dark:text-amber-400 shrink-0" />
      <p className="text-[13px] font-medium text-slate-700 dark:text-slate-200">
        {label}{timeText}
      </p>
    </div>
  );
}

/* ── Recursos espirituales ────────────────────────────────────────────────── */
const SPIRITUAL_RESOURCES = [
  { icon: BookOpen,   label: "Biblia",            to: "/member/biblia?from=pastor",    iconBg: "bg-[#e3eefb] dark:bg-blue-900/20",     iconColor: "text-[#1b4f9c] dark:text-blue-400"     },
  { icon: Sun,        label: "Devocional EGW",    to: "/member/devocional?from=pastor",iconBg: "bg-amber-50 dark:bg-amber-900/20",      iconColor: "text-amber-600 dark:text-amber-400"    },
  { icon: Music,      label: "Himnario",          to: "/member/himnario?from=pastor",  iconBg: "bg-violet-50 dark:bg-violet-900/20",    iconColor: "text-violet-600 dark:text-violet-400"  },
  { icon: BookMarked, label: "Escuela Sabática",  to: "/member/escuela?from=pastor",   iconBg: "bg-indigo-50 dark:bg-indigo-900/20",    iconColor: "text-indigo-600 dark:text-indigo-400"  },
  { icon: Star,       label: "28 Creencias",      to: "/member/creencias?from=pastor", iconBg: "bg-emerald-50 dark:bg-emerald-900/20",  iconColor: "text-emerald-600 dark:text-emerald-400"},
  { icon: FileText,   label: "Manual de Iglesia", to: "/member/manual?from=pastor",    iconBg: "bg-slate-100 dark:bg-slate-700/50",     iconColor: "text-slate-600 dark:text-slate-400"    },
];

export default function Dashboard() {
  const { districtId, district, userProfile } = useDistrictGuard();
  const navigate = useNavigate();

  const { isOnline, pendingSync } = useOfflineStatus();
  const { todayPreachings } = useTodayPreaching(userProfile?.person_id, districtId);
  const { reading, read, lessonRead, markReadingDone, markLessonDone } = useSpiritualProgress();
  const { lesson, quarterEnded } = useSabbathSchool();

  const { unreadCount: notifUnreadCount } = useNotificationsCtx();

  const [unreadMessages, setUnreadMessages] = useState(0);
  useEffect(() => {
    if (!districtId) return;
    getUnreadMemberMessageCount().then(setUnreadMessages).catch(() => {});
  }, [districtId]);

  const {
    loading: dashboardLoading,
    families,
    people,
    visits,
    totalChurches,
    totalFamilies,
    urgentCount,
    todayPlan,
    todayVisits,
    todayEvents,
    todayImportantCount,
    upcomingVisits,
    upcomingPersonalEvents,
    upcomingMinisterialEvents,
    birthdaysToday,
    birthdaysUpcoming,
    anniversariesToday,
    anniversariesUpcoming,
    baptismsToday,
    baptismsUpcoming,
    firstName,
  } = useDashboardData({ districtId, district, userProfile });

  const openNotifs = useCallback(
    () => window.dispatchEvent(new CustomEvent("keves:openNotifications")),
    []
  );

  // Kevia dynamic context — registered while dashboard is mounted
  const keviaCtx = useMemo(() => {
    if (dashboardLoading) return null;
    const pendingCount = visits.filter((v) => v.status === "pendiente").length;
    const lines = [
      "Módulo: Dashboard pastoral",
      `  Iglesias en el distrito: ${totalChurches}`,
      `  Familias registradas: ${totalFamilies}`,
      `  Personas registradas: ${people.length}`,
      `  Visitas pendientes: ${pendingCount}`,
      urgentCount > 0 && `  Prioridades urgentes hoy: ${urgentCount}`,
    ].filter(Boolean);
    return {
      type:         "module",
      id:           "dashboard",
      name:         "Dashboard",
      tone:         urgentCount > 0 ? "followup_urgent" : "neutral",
      contextBlock: `[Contexto activo en Kéves]\n${lines.join("\n")}\n\n[Pregunta del pastor]\n`,
    };
  }, [dashboardLoading, totalChurches, totalFamilies, people?.length, visits, urgentCount]);
  useRegisterKeviaContext(keviaCtx);

  return (
    <PageLayout>

      {/* ── HEADER ──────────────────────────────────────── */}
      <DashboardHeader
        pendingSync={pendingSync}
        isOnline={isOnline}
      />

      {/* ── HERO ────────────────────────────────────────── */}
      <DashboardHero
        firstName={firstName}
        district={district}
        notifCount={notifUnreadCount}
        onNotifClick={openNotifs}
      />

      {/* ── BÚSQUEDA ────────────────────────────────────── */}
      <DashboardSearch />

      {/* ── BIENVENIDA (primera vez) ─────────────────────── */}
      <WelcomeBanner />

      {/* ── MENSAJES NO LEÍDOS ─────────────────────────────── */}
      {unreadMessages > 0 && (
        <div className="px-4 pt-4">
          <Link
            to="/member-messages"
            className="flex items-center gap-3 bg-white dark:bg-slate-800 rounded-[24px] p-4 border border-brand/20 dark:border-blue-500/20 shadow-[0_2px_12px_rgba(27,79,156,0.08)] transition-all duration-300 ease-out hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] active:scale-[0.98] active:opacity-80"
          >
            <div className="relative shrink-0">
              <div className="h-11 w-11 rounded-[14px] bg-brand/10 dark:bg-brand/20 flex items-center justify-center">
                <MessageSquare size={20} className="text-brand dark:text-blue-400" />
              </div>
              <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                {unreadMessages > 99 ? "99+" : unreadMessages}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-semibold text-slate-800 dark:text-slate-100 leading-tight">
                {unreadMessages === 1 ? "1 mensaje nuevo" : `${unreadMessages} mensajes nuevos`} de miembros
              </p>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">Toca para leer</p>
            </div>
            <ChevronRight size={16} className="text-slate-300 dark:text-slate-600 shrink-0" />
          </Link>
        </div>
      )}

      {/* ── CONTENIDO PASTORAL ──────────────────────────── */}
      <div className="pt-5 pb-[calc(96px+env(safe-area-inset-bottom))]">

        {/* ── HOY ── atención pastoral inmediata ── */}
        <div className="px-4 mb-2.5">
          <SectionTitle title="Hoy" icon={CalendarCheck2} />
        </div>
        <SabbathCountdown />
        {dashboardLoading ? (
          <div className="px-4">
            <div className="rounded-[20px] bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700/60 p-4 animate-pulse shadow-card">
              <div className="flex items-start gap-3.5">
                <div className="h-10 w-10 rounded-[12px] bg-slate-100 dark:bg-slate-700 shrink-0" />
                <div className="flex-1 space-y-2 pt-1">
                  <div className="h-2.5 w-20 bg-slate-100 dark:bg-slate-700 rounded-full" />
                  <div className="h-4 w-3/4 bg-slate-100 dark:bg-slate-700 rounded-full" />
                  <div className="h-2.5 w-1/2 bg-slate-100 dark:bg-slate-700 rounded-full" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <TodayPriority
            urgentCount={urgentCount + todayPreachings.length}
            todayPlan={todayPlan}
            todayVisits={todayVisits}
            todayEvents={todayEvents}
            todayImportantCount={todayImportantCount}
            birthdaysToday={birthdaysToday}
            baptismsToday={baptismsToday}
            anniversariesToday={anniversariesToday}
            anniversariesUpcoming={anniversariesUpcoming}
            todayPreachings={todayPreachings}
            navigate={navigate}
          />
        )}

        {/* ── PLAN PASTORAL HOY ── */}
        <div className="px-4 pt-6 mb-2.5">
          <SectionTitle title="Plan pastoral hoy" icon={CalendarCheck2} />
        </div>
        <PastoralPlanToday />

        {/* ── CRECIMIENTO ESPIRITUAL ── */}
        <div className="px-4 pt-6 mb-2.5">
          <SectionTitle title="Crecimiento espiritual" icon={BookOpen} />
        </div>
        <SpiritualGrowthCard
          reading={reading}
          read={read}
          lessonRead={lessonRead}
          markReadingDone={markReadingDone}
          markLessonDone={markLessonDone}
          lesson={lesson}
          quarterEnded={quarterEnded}
        />

        {/* ── RECURSOS ESPIRITUALES ── */}
        <div className="px-4 pt-6 mb-2.5">
          <SectionTitle title="Recursos espirituales" icon={BookOpen} />
        </div>
        <div className="px-4">
          <div className="grid grid-cols-3 gap-2">
            {SPIRITUAL_RESOURCES.map(({ icon: Icon, label, to, iconBg, iconColor }) => (
              <Link
                key={to}
                to={to}
                className="flex flex-col items-center gap-2 py-4 bg-white dark:bg-slate-800 rounded-[20px] border border-slate-200/50 dark:border-slate-700/50 shadow-sm transition-all duration-200 active:scale-[0.97] active:opacity-80"
              >
                <div className={`h-10 w-10 rounded-[14px] flex items-center justify-center ${iconBg}`}>
                  <Icon size={18} className={iconColor} />
                </div>
                <p className="text-[11px] font-medium text-slate-600 dark:text-slate-300 text-center leading-tight px-1">{label}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* ── ESTA SEMANA ── horizonte pastoral ── */}
        <div className="pt-6">
          <UpcomingAgenda
            upcomingPersonalEvents={upcomingPersonalEvents}
            upcomingMinisterialEvents={upcomingMinisterialEvents}
            upcomingVisits={upcomingVisits}
            navigate={navigate}
          />
        </div>

        {/* ── FECHAS RELACIONALES ── */}
        <div className="pt-6">
          <ImportantDates
            birthdaysToday={birthdaysToday}
            birthdaysUpcoming={birthdaysUpcoming}
            anniversariesToday={anniversariesToday}
            anniversariesUpcoming={anniversariesUpcoming}
            baptismsUpcoming={baptismsUpcoming}
            families={families}
            navigate={navigate}
          />
        </div>

      </div>

    </PageLayout>
  );
}
